import React, { useState, useMemo } from 'react'
import { Box, Text, Flex, Stack, Badge, Collapse } from '@chakra-ui/core'
import { ChevronDown, ChevronUp, Search } from 'lucide-react'
import { NoFocusButton } from 'components/CommonComponents/NoFocusButton'
import { RootState } from 'constants/interfaces'
import { useSelector } from 'react-redux'
import { InputWithIcon } from '../commonUI/InputWithIcon'
import useLocaleText from 'components/useLocaleText'
import { Campaign } from 'redux/reducers/campaigns'
import ExcelJS from 'exceljs'
import { saveAs } from 'file-saver'
import ExportToExcellButton from 'components/Reports/components/ExportToExcellButton'
import { CampaignData } from 'redux/actions/campaigns'

const OverviewCampaignFields = ({ campaignData, campaign }: { campaignData: CampaignData; campaign: Campaign }) => {
  const { groups } = useSelector((state: RootState) => state.config.config)
  const [productsCollapsed, setProductsCollapsed] = useState(false)
  const [contextsCollapsed, setContextsCollapsed] = useState(false)
  const [contextSearch, setContextSearch] = useState('')
  const t_search = useLocaleText('search')

  const { is_store_specific } = campaign
  const { products_details, contexts, item_type } = campaignData

  const item_id = products_details?.item_id || []
  const { context_id, context_name } = contexts || {}

  const shouldCollapseProducts = item_id && item_id.length > 3
  const shouldCollapseContexts = context_name && context_name.length > 3

  const filteredContexts = useMemo(() => {
    if (!contextSearch) return context_name
    return context_name.filter((name) => name.toLowerCase().includes(contextSearch.toLowerCase()))
  }, [context_name, contextSearch])

  const exportProductsToExcel = async () => {
    const workbook = new ExcelJS.Workbook()
    const worksheet = workbook.addWorksheet('Products Data')

    worksheet.columns = [
      { header: 'Item Type', key: 'itemType', width: 15 },
      { header: 'Item ID', key: 'itemId', width: 15 },
    ]

    item_id.forEach((id) => {
      worksheet.addRow({
        itemType: item_type,
        itemId: id,
      })
    })

    worksheet.getRow(1).font = { bold: true }
    worksheet.getRow(1).fill = {
      type: 'pattern',
      pattern: 'solid',
      fgColor: { argb: 'FFE0E0E0' },
    }

    const buffer = await workbook.xlsx.writeBuffer()
    const blob = new Blob([buffer], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' })
    saveAs(blob, `campaign_products_${campaign.title || 'data'}_${new Date().toISOString().split('T')[0]}.xlsx`)
  }

  const exportContextsToExcel = async () => {
    const workbook = new ExcelJS.Workbook()
    const worksheet = workbook.addWorksheet('Contexts Data')

    worksheet.columns = [
      { header: 'Store Name', key: 'storeName', width: 20 },
      { header: 'Store ID', key: 'storeId', width: 15 },
    ]

    if (is_store_specific && context_name) {
      context_name.forEach((name, index) => {
        const contextId = context_id[index]
        const sids = groups[contextId]?.sid || []

        if (sids.length > 0) {
          sids.forEach((sid) => {
            const row = worksheet.addRow({
              storeName: name,
              storeId: sid,
            })

            row.getCell('storeName').font = { bold: true }

            row.getCell('storeId').alignment = {
              horizontal: 'center',
              vertical: 'middle',
            }
          })
        } else {
          const row = worksheet.addRow({
            storeName: name,
            storeId: '',
          })

          row.getCell('storeName').fill = {
            type: 'pattern',
            pattern: 'solid',
          }
          row.getCell('storeName').font = { bold: true }

          row.getCell('storeId').alignment = {
            horizontal: 'center',
            vertical: 'middle',
          }
        }
      })
    }

    worksheet.getRow(1).font = { bold: true }
    worksheet.getRow(1).fill = {
      type: 'pattern',
      pattern: 'solid',
      fgColor: { argb: 'FFE0E0E0' },
    }

    const buffer = await workbook.xlsx.writeBuffer()
    const blob = new Blob([buffer], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' })
    saveAs(blob, `campaign_contexts_${campaign.title || 'data'}_${new Date().toISOString().split('T')[0]}.xlsx`)
  }

  return (
    <Stack spacing={6} align="stretch" w="100%">
      <Box border="1px solid" borderColor="gray.200" borderRadius="md" bg="white" overflow="hidden">
        <Flex justify="space-between" align="center" p={4} borderColor="gray.200">
          <Text fontWeight="bold" fontSize="lg" textTransform="capitalize">
            {item_type} ({item_id.length})
          </Text>
          <Flex align="center" style={{ gap: '8px' }}>
            <ExportToExcellButton handleExport={exportProductsToExcel} isLoading={false} isDisabled={false} />
            {shouldCollapseProducts && (
              <NoFocusButton
                aria-label="Toggle products section"
                variant="ghost"
                color="gray.500"
                _hover={{
                  bg: 'transparent',
                }}
                onClick={() => setProductsCollapsed(!productsCollapsed)}
              >
                {productsCollapsed ? <ChevronDown /> : <ChevronUp />}
              </NoFocusButton>
            )}
          </Flex>
        </Flex>

        <Collapse isOpen={true}>
          <Box p={4}>
            <Stack spacing={4} align="stretch">
              {item_id
                .slice(0, productsCollapsed && shouldCollapseProducts ? Math.min(3, item_id.length) : item_id.length)
                .map((id, index) => (
                  <Box key={index} p={4} border="1px solid" borderColor="gray.200" borderRadius="md">
                    <Flex style={{ gap: '16px' }}>
                      <Box flex={1}>
                        <Text fontSize="sm" color="gray.600" mb={1}>
                          Item ID
                        </Text>
                        <Box bg="white" borderColor="gray.300" fontSize="sm">
                          <Text fontWeight="bold">{id}</Text>
                        </Box>
                      </Box>
                    </Flex>
                  </Box>
                ))}
            </Stack>
          </Box>
        </Collapse>
      </Box>

      {is_store_specific && (
        <Box border="1px solid" borderColor="gray.200" borderRadius="md" bg="white" overflow="hidden">
          <Flex justify="space-between" align="center" p={4} borderColor="gray.200">
            <Text fontWeight="bold" fontSize="lg">
              Contexts ({context_name.length})
            </Text>
            <Flex align="center" style={{ gap: '8px' }}>
              <ExportToExcellButton handleExport={exportContextsToExcel} isLoading={false} isDisabled={false} />
              {shouldCollapseContexts && (
                <NoFocusButton
                  aria-label="Toggle contexts section"
                  variant="ghost"
                  color="gray.500"
                  onClick={() => setContextsCollapsed(!contextsCollapsed)}
                  _hover={{
                    bg: 'transparent',
                  }}
                >
                  {contextsCollapsed ? <ChevronDown /> : <ChevronUp />}
                </NoFocusButton>
              )}
            </Flex>
          </Flex>

          <Collapse isOpen={true}>
            <Box p={4}>
              <Stack spacing={4} align="stretch">
                <InputWithIcon
                  icon={<Search />}
                  value={contextSearch}
                  onChange={(e: React.ChangeEvent<HTMLInputElement>) => setContextSearch(e.target.value)}
                  borderRadius="5px"
                  placeholder={t_search}
                />

                <Stack spacing={2} align="stretch" overflowY="auto">
                  {filteredContexts
                    .slice(
                      0,
                      contextsCollapsed && shouldCollapseContexts
                        ? Math.min(3, filteredContexts.length)
                        : filteredContexts.length
                    )
                    .map((name, index) => {
                      const originalIndex = context_name.indexOf(name)
                      const id = context_id[originalIndex]
                      const sids = groups[id]?.sid

                      return (
                        <Box key={index} p={3} border="1px solid" borderColor="gray.200" borderRadius="md" bg="white">
                          <Text fontWeight="bold" fontSize="lg" mb={2}>
                            {name}
                          </Text>
                          {sids && (
                            <Flex wrap="wrap" style={{ gap: '8px' }}>
                              {sids.map((sid) => (
                                <Badge
                                  key={sid}
                                  variant="outline"
                                  fontSize="xs"
                                  fontFamily="mono"
                                  bg="gray.20"
                                  color="gray.700"
                                >
                                  {sid}
                                </Badge>
                              ))}
                            </Flex>
                          )}
                        </Box>
                      )
                    })}
                </Stack>
              </Stack>
            </Box>
          </Collapse>
        </Box>
      )}
    </Stack>
  )
}

export default OverviewCampaignFields
